import { formatAmount } from '../format/money';
import { formatPct, resolveDiscount, type DiscountInput, type ResolvedDiscount } from './discount';
import {
  claimedUnitIds,
  garmentDiscount,
  namedAccessories,
  type BookingDraft,
  type DraftAccessory,
  type DraftGarment,
} from './draft';

/**
 * One garment in a booking being taken, opened from the bag (the booking redesign's per-garment
 * sheet): what it comes to, its own discount, what goes out with it, who wears it — and the three
 * ways to change it: swap it for another garment, move it to another person, take it out.
 *
 * <p>
 * The website's sheet and the phone's read this, and the two editors inside it (a garment's own
 * discount, its accessories) are checked here rather than in either screen, so the phone cannot
 * accept a line the website would refuse.
 * </p>
 */

export interface GarmentSheet {
  unitId: number;
  itemId: number;
  /** The design's name — the sheet's heading. */
  name: string;
  /** The design with its variant, as the rail named it. */
  variantName: string;
  serialCode: string;
  referenceNumber?: string | null;
  imageUrl: string | null;
  categoryName: string;
  wearerKey: string;
  wearerName: string;
  /** The garment's own rate for the charged days; null when nothing prices it. */
  rate: number | null;
  /** What its own discount takes off. Zero when none. */
  off: number;
  /** The rate less its own discount; null when nothing prices it. */
  charged: number | null;
  /** "None", "10% · ₹650 off" or "₹500 off". */
  discountWords: string;
  /** "Nothing goes out with it", or the named accessories ("Kalgi, Cufflinks × 2"). */
  accessoryWords: string;
  /** False when the clerk may give nothing, or there is no rate to take it off. */
  canDiscount: boolean;
  /** Everyone else in the party — where Move can send it. */
  movesTo: { key: string; name: string }[];
}

const nameOf = (name: string) => name.trim() || 'Unnamed';

export function garmentSheet(
  draft: BookingDraft,
  unitId: number,
  currency = '',
): GarmentSheet | null {
  const wearer = draft.wearers.find((each) =>
    each.garments.some((garment) => garment.unitId === unitId),
  );
  const garment = wearer?.garments.find((each) => each.unitId === unitId);

  if (!wearer || !garment) {
    return null;
  }

  const off = garmentDiscount(garment);
  const accessories = namedAccessories(garment);

  return {
    unitId,
    itemId: garment.itemId,
    name: garment.designName ?? garment.itemName,
    variantName: garment.itemName,
    serialCode: garment.serialCode,
    ...(garment.referenceNumber ? { referenceNumber: garment.referenceNumber } : {}),
    imageUrl: garment.imageUrl ?? null,
    categoryName: garment.categoryName ?? '',
    wearerKey: wearer.key,
    wearerName: nameOf(wearer.name),
    rate: garment.rate,
    off,
    charged: garment.rate === null ? null : Math.max(0, garment.rate - off),
    discountWords:
      off <= 0
        ? 'None'
        : garment.discountPct !== null
          ? `${formatPct(garment.discountPct)}% · ${currency}${formatAmount(off)} off`
          : `${currency}${formatAmount(off)} off`,
    accessoryWords:
      accessories.length === 0
        ? 'Nothing goes out with it'
        : accessories
            .map((each) => (each.quantity > 1 ? `${each.name} × ${each.quantity}` : each.name))
            .join(', '),
    canDiscount: draft.discountCeilingPct > 0 && garment.rate !== null,
    movesTo: draft.wearers
      .filter((each) => each.key !== wearer.key)
      .map((each) => ({ key: each.key, name: nameOf(each.name) })),
  };
}

/**
 * Hands a garment to someone else in the party, with its discount and accessories — the garment is
 * the same one, only who wears it changed. Unchanged when either end is not in the draft.
 */
export function moveGarment(draft: BookingDraft, unitId: number, toKey: string): BookingDraft {
  const garment = draft.wearers
    .flatMap((wearer) => wearer.garments)
    .find((each) => each.unitId === unitId);

  if (!garment || !draft.wearers.some((wearer) => wearer.key === toKey)) {
    return draft;
  }

  return {
    ...draft,
    wearers: draft.wearers.map((wearer) => {
      const without = wearer.garments.filter((each) => each.unitId !== unitId);

      return wearer.key === toKey
        ? { ...wearer, garments: [...without, garment] }
        : { ...wearer, garments: without };
    }),
  };
}

/**
 * Puts another garment in this one's place: same person, same position in their list, and the same
 * accessories, since the kalgi goes out with whatever sherwani the groom ends up in.
 *
 * <p>
 * <strong>Its own discount stays only within the design.</strong> "Take ₹500 off that sherwani" is
 * still true of the same sherwani in a 42; it was never said about a bandhgala, and a flat ₹500
 * carried onto a ₹2,500 garment is a 20% concession nobody gave.
 * </p>
 *
 * <p>
 * Unchanged when the old garment is not in the draft, or the new one is already in it — two people
 * holding one tuxedo is the mistake {@link addGarment} refuses for the same reason.
 * </p>
 */
export function swapGarment(
  draft: BookingDraft,
  unitId: number,
  replacement: DraftGarment,
): BookingDraft {
  const claimed = claimedUnitIds(draft);

  if (!claimed.has(unitId) || (replacement.unitId !== unitId && claimed.has(replacement.unitId))) {
    return draft;
  }

  return {
    ...draft,
    wearers: draft.wearers.map((wearer) => ({
      ...wearer,
      garments: wearer.garments.map((garment) => {
        if (garment.unitId !== unitId) {
          return garment;
        }

        const sameDesign = garment.itemId === replacement.itemId;

        return {
          ...replacement,
          accessories: garment.accessories,
          discountPct: sameDesign ? garment.discountPct : null,
          discountAmount: sameDesign ? garment.discountAmount : null,
        };
      }),
    })),
  };
}

/** A garment's own discount, as typed into its editor, checked. */
export interface GarmentDiscountCheck {
  /** What it resolves to against the garment's rate; null when nothing usable was typed. */
  resolved: ResolvedDiscount | null;
  /** Why it cannot be applied; null when it can (or when nothing was typed yet). */
  problem: string | null;
}

/**
 * Checks a garment's own discount before it is applied.
 *
 * <p>
 * <strong>The ceiling is the booking's, and it bounds the whole concession.</strong> What this can
 * promise is that the line alone is inside the limit; the combined figure is checked where the
 * total is known (`draftProblems`), and by the server regardless.
 * </p>
 */
export function checkGarmentDiscount(
  rate: number,
  input: DiscountInput,
  ceilingPct: number,
): GarmentDiscountCheck {
  const typed = input.pct ?? input.amount;

  if (typed === null || !Number.isFinite(typed)) {
    return { resolved: null, problem: null };
  }

  if (typed < 0) {
    return {
      resolved: null,
      problem: 'A discount cannot be negative. To charge more, change the rate card.',
    };
  }

  const resolved = resolveDiscount(rate, input);

  if ((resolved?.effectivePct ?? 0) > Math.max(0, ceilingPct)) {
    return {
      resolved,
      problem: `Your role allows ${formatPct(ceilingPct)}% — and that limit is on the booking's total discount, so giving it on each garment does not get past it.`,
    };
  }

  return { resolved, problem: null };
}

/**
 * What stops an accessory list being saved, or null. Only named rows are checked: a blank row is the
 * clerk having pressed Add and not typed yet, and it is dropped on the way out rather than refused.
 */
export function accessoriesProblem(rows: readonly DraftAccessory[]): string | null {
  const named = rows.filter((row) => row.name.trim().length > 0);

  if (named.some((row) => !Number.isInteger(row.quantity) || row.quantity < 1)) {
    return 'Every accessory needs a quantity of at least one.';
  }

  if (named.some((row) => (row.replacementCharge ?? 0) < 0)) {
    return 'A replacement charge cannot be negative.';
  }

  return null;
}
